import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const tourSteps = [
  {
    target: '[data-tour="dashboard"]',
    title: "Welcome to Your Dashboard",
    content: "This is your home base. Here you'll see an overview of your customers, quotes, invoices, and total revenue at a glance.",
    position: "bottom"
  },
  {
    target: '[data-tour="nav-customers"]',
    title: "Manage Customers",
    content: "Click here to view, add, and manage your customers. You can store contact details, addresses, and track their quote/invoice history.",
    position: "right"
  },
  {
    target: '[data-tour="nav-items"]',
    title: "Item Catalog",
    content: "Build a library of your commonly used items and services. This saves time when creating quotes - just search and add!",
    position: "right"
  },
  {
    target: '[data-tour="nav-quotes"]',
    title: "Create Quotes",
    content: "Create professional quotes for your customers. Add line items, materials costs, and send them via email. When accepted, convert to an invoice with one click!",
    position: "right"
  },
  {
    target: '[data-tour="nav-invoices"]',
    title: "Manage Invoices",
    content: "Track all your invoices here. See payment status, record payments, send reminders, and download paid invoices in bulk.",
    position: "right"
  },
  {
    target: '[data-tour="nav-settings"]',
    title: "Customize Settings",
    content: "Set up your company details, logo, invoice terms, manage suppliers, and configure payment options.",
    position: "right"
  },
  {
    target: '[data-tour="user-menu"]',
    title: "Your Account",
    content: "Access your account settings, contact support, view this guide again, or log out from here.",
    position: "left"
  }
];

const GuidedTour = ({ open, onOpenChange, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  const updateTargetPosition = useCallback(() => {
    if (!open) return;
    
    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTargetRect(rect);
      setIsVisible(true);
    } else {
      // If target not found, try to make it visible or skip
      setIsVisible(false);
    }
  }, [open, step.target]);

  useEffect(() => {
    if (open) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(updateTargetPosition, 100);
      
      // Update position on scroll or resize
      window.addEventListener('scroll', updateTargetPosition, true);
      window.addEventListener('resize', updateTargetPosition);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', updateTargetPosition, true);
        window.removeEventListener('resize', updateTargetPosition);
      };
    }
  }, [open, currentStep, updateTargetPosition]);

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setIsVisible(false);
    onOpenChange(false);
  };

  const handleComplete = () => {
    setCurrentStep(0);
    setIsVisible(false);
    onOpenChange(false);
    if (onComplete) onComplete();
  };

  const getTooltipPosition = () => {
    if (!targetRect) return { top: '50%', left: '50%' };

    const padding = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 180;

    let top, left;

    switch (step.position) {
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.right + padding;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'bottom':
        top = targetRect.bottom + padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      default:
        top = targetRect.bottom + padding;
        left = targetRect.left;
    }

    // Keep tooltip within viewport
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    return { top: `${top}px`, left: `${left}px` };
  };

  const getArrowPosition = () => {
    switch (step.position) {
      case 'right':
        return 'left-0 top-1/2 -translate-x-full -translate-y-1/2 border-r-white border-t-transparent border-b-transparent border-l-transparent border-8';
      case 'left':
        return 'right-0 top-1/2 translate-x-full -translate-y-1/2 border-l-white border-t-transparent border-b-transparent border-r-transparent border-8';
      case 'bottom':
        return 'top-0 left-1/2 -translate-y-full -translate-x-1/2 border-b-white border-l-transparent border-r-transparent border-t-transparent border-8';
      case 'top':
        return 'bottom-0 left-1/2 translate-y-full -translate-x-1/2 border-t-white border-l-transparent border-r-transparent border-b-transparent border-8';
      default:
        return '';
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9998]"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={handleClose}
      />

      {/* Highlight cutout for target element */}
      {targetRect && isVisible && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            borderRadius: '8px',
            border: '2px solid #3B82F6',
            backgroundColor: 'transparent'
          }}
        />
      )}

      {/* Tooltip */}
      {isVisible && (
        <div
          className="fixed z-[10000] w-80 bg-white rounded-xl shadow-2xl"
          style={getTooltipPosition()}
          data-testid="tour-tooltip"
        >
          {/* Arrow */}
          <div className={`absolute w-0 h-0 ${getArrowPosition()}`} />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="tour-close"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>

          {/* Content */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-blue-600 font-medium">
                Step {currentStep + 1} of {tourSteps.length}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {step.title}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {step.content}
            </p>

            {/* Progress bar */}
            <div className="w-full h-1 bg-gray-200 rounded-full mb-4">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
              />
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                disabled={isFirstStep}
                className="gap-1"
                data-testid="tour-prev"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  data-testid="tour-skip"
                >
                  Skip tour
                </Button>
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="gap-1"
                  data-testid="tour-next"
                >
                  {isLastStep ? "Finish" : "Next"}
                  {!isLastStep && <ChevronRight className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default GuidedTour;
